import { ArrowLeft } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ButtonCmp from 'src/components/ButtonCmp';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import StepperLayout from 'src/components/StepperLayout/StepperLayout';
import { PATH } from 'src/constants/path';
import { selectCommonCarrierRequiredFields } from 'src/redux/address.slice';
import { completeStep, goToStep, nextStep, prevStep, resetForm, selectCurrentStep, selectFormData } from 'src/redux/bookingStepper.slice';
import { isShipperConsigneeAddressValid } from 'src/utils/CommonFunctions';

import { BOOKING_KEYS, getFilteredBookingSteps } from './bookingQuote.const';
import Sidebar from './Sidebar';
import BolRequirements from './Steps/BolRequirements';
import { hasEmptyRequiredBolField } from './Steps/BolRequirements/fieldRegistry.config';
import Dimensions from './Steps/Dimensions';
import OrderDetails from './Steps/OrderDetails';
import ReviewFinalize from './Steps/ReviewFinalize/ReviewFinalize';
import ShipperConsigneeDetails from './Steps/ShipperConsigneeDetails/ShipperConsigneeDetails';
import BookingStopDetails from './Steps/StopDetails/BookingStopDetails';
import UploadDocuments from './Steps/UploadDocuments';

const BookingStepper = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const currentStep = useSelector(selectCurrentStep);
    const commonCarrierRequiredFields = useSelector(selectCommonCarrierRequiredFields);
    const bookingFormData = useSelector(selectFormData);
    const quoteId = bookingFormData?.quoteId || '';

    const shipperCountryCode = bookingFormData?.shipperDetails?.shipperCountry?.toLowerCase() || bookingFormData?.shipperDetails?.shipperCountryCode?.toLowerCase();
    const consigneeCountryCode = bookingFormData?.consigneeDetails?.consigneeCountry?.toLowerCase() || bookingFormData?.consigneeDetails?.consigneeCountryCode?.toLowerCase();
    const isCrossBorder = useMemo(() => !!shipperCountryCode && !!consigneeCountryCode && shipperCountryCode !== consigneeCountryCode, [shipperCountryCode, consigneeCountryCode]);

    // Client decision — read-only "Stop N" steps display the quote's existing middle stops
    // (already filtered server-side to customer-requested ones only — see
    // wal-customer-portal-be's filterCustomerVisibleStops). bookingFormData.stops is seeded once
    // in QuoteDetail/index.tsx's handleBookOrder and never touched again here.
    const middleStops = bookingFormData?.stops || [];
    // Sorted copy — .sort mutates, and middleStops is Redux state. Carries city/state through so the
    // sidebar tab can read "Stop 2 · Dennis Port, MA"; the step list itself only needs the order.
    const sortedStops = useMemo(() => [...middleStops].sort((a: any, b: any) => a.position - b.position), [middleStops]);

    const steps = useMemo(() => getFilteredBookingSteps(t, isCrossBorder, sortedStops), [t, isCrossBorder, sortedStops]);
    const currentStepInfo = steps[currentStep - 1];

    // Ref to call into the active step's validation/save logic
    const stepperFooterRef = useRef<any>(null);
    const [isBookingQuote, setIsBookingQuote] = useState(false);
    // Tracks which step needs its validation shown after a cross-step navigation
    const [pendingValidation, setPendingValidation] = useState<number | null>(null);
    const [isStepFormEditing, setIsStepFormEditing] = useState(false);

    useEffect(() => {
        if (!quoteId) {
            navigate(PATH.MY_QUOTE, { replace: true });
        }
    }, [quoteId]);

    useEffect(() => {
        setIsStepFormEditing(false);
    }, [currentStep]);

    // Bug fix (this feature) — these used to be hardcoded literals (1/2/3), which only worked
    // because the step order was fixed. Injecting a variable number of "Stop N" steps between
    // Shipper and Consignee shifts every step id after Shipper, so Consignee/Order Details' ids
    // must be looked up from the actual (already stop-aware) `steps` list instead.
    const shipperStepId = steps.find((step) => step.key === BOOKING_KEYS.SHIPPER_DETAILS)?.id ?? 1;
    const consigneeStepId = steps.find((step) => step.key === BOOKING_KEYS.CONSIGNEE_DETAILS)?.id ?? 2;
    const orderDetailsStepId = steps.find((step) => step.key === BOOKING_KEYS.ORDER_DETAILS)?.id ?? 3;
    const bolRequirementsStepId = steps.find((step) => step.key === BOOKING_KEYS.BOL_REQUIREMENTS)?.id ?? steps.length;

    // Check required fields across all steps before allowing final booking.
    // The sidebar lets users jump freely, so this guards against submitting
    // with incomplete earlier steps (e.g. consignee delivery date never filled).
    // Address completeness uses the shared isShipperConsigneeAddressValid rule
    // so the booking gate matches the per-step auto-open-edit behavior.
    const validateRequiredStepsBeforeBooking = (): { isValid: boolean; failingStep: number } => {
        if (!bookingFormData?.shipperDetails?.pickupDate) {
            return { isValid: false, failingStep: shipperStepId };
        }

        const s = bookingFormData?.shipperDetails;
        const isShipperAddressValid = isShipperConsigneeAddressValid({
            fullAddress: s?.shipperFullAddress,
            latitude: s?.shipperLatitude,
            longitude: s?.shipperLongitude,
            postal: s?.shipperPostal,
            streetNumber: s?.shipperStreetNumber,
            state: s?.shipperState,
            stateCode: s?.shipperStateCode,
            country: s?.shipperCountry,
            countryCode: s?.shipperCountryCode,
            city: s?.shipperCity,
        });

        if (!isShipperAddressValid) {
            return { isValid: false, failingStep: shipperStepId };
        }

        const c = bookingFormData?.consigneeDetails;
        const isConsigneeAddressValid = isShipperConsigneeAddressValid({
            fullAddress: c?.consigneeFullAddress,
            latitude: c?.consigneeLatitude,
            longitude: c?.consigneeLongitude,
            postal: c?.consigneePostal,
            streetNumber: c?.consigneeStreetNumber,
            state: c?.consigneeState,
            stateCode: c?.consigneeStateCode,
            country: c?.consigneeCountry,
            countryCode: c?.consigneeCountryCode,
            city: c?.consigneeCity,
        });

        if (!isConsigneeAddressValid) {
            return { isValid: false, failingStep: consigneeStepId };
        }

        if (bookingFormData?.orderDetails?.isLoadInsuranceEnabled) {
            const insuranceCommodity = bookingFormData?.orderDetails?.insuranceCommodity;
            const hasCommodity = Array.isArray(insuranceCommodity) ? insuranceCommodity.length > 0 : typeof insuranceCommodity === 'string' && insuranceCommodity.trim().length > 0;

            if (!hasCommodity || !bookingFormData?.orderDetails?.insuranceCommercialValue) {
                return { isValid: false, failingStep: orderDetailsStepId };
            }
        }

        // Bug fix — the required BOL contact fields are rejected blank by the API, but this gate never
        // covered them. The step's own Next validates, so the only way through was the sidebar: jump
        // straight to Review & Confirm, never open BOL Requirements, and submit.
        // BOL Instructions is deliberately excluded (isRequired: false in the field registry) — it is
        // optional on both sides now, so a blank one must never bounce the user back to this step.
        if (hasEmptyRequiredBolField(commonCarrierRequiredFields, bookingFormData?.carrierSlug ?? null, bookingFormData)) {
            return { isValid: false, failingStep: bolRequirementsStepId };
        }

        return { isValid: true, failingStep: 0 };
    };

    // After navigating to a failing step, trigger its form validation so the
    // red border appears on the missing field. React guarantees the child's
    // useEffect (which registers triggerValidation into stepperFooterRef) runs
    // before this parent effect, so the ref is safe to read here.
    useEffect(() => {
        if (pendingValidation !== null && currentStep === pendingValidation) {
            const triggerValidation = stepperFooterRef.current?.triggerValidation;

            if (triggerValidation) {
                setPendingValidation(null);
                triggerValidation();
            }
        }
    }, [currentStep, pendingValidation]);

    const handleExitFlow = () => {
        dispatch(resetForm());
        navigate(`${PATH.MY_QUOTE}/${quoteId}`, { replace: true });
    };

    // Save the current step's form data before jumping to an arbitrary step via the
    // sidebar. Without this, filling in (e.g.) the pickup date and then clicking a
    // sidebar item would discard the unsaved value, causing an infinite redirect loop
    // where validateRequiredStepsBeforeBooking keeps sending the user back to step 1.
    const handleSidebarStepNavigate = async (stepId: number) => {
        if (stepperFooterRef?.current?.triggerBack) {
            try {
                await stepperFooterRef.current.triggerBack();
            } catch {
                // Ignore save errors — always allow navigation
            }
        }
        dispatch(goToStep(stepId));
    };

    const handleBack = async () => {
        // Let the current step save its state before going back
        if (stepperFooterRef?.current?.triggerBack) {
            try {
                await stepperFooterRef.current.triggerBack();
            } catch {
                // Ignore back errors — always navigate back
            }
        }

        if (currentStep > 1) {
            dispatch(prevStep());
        }
    };

    const handleNext = async () => {
        const isLastStep = currentStep === steps.length;

        if (isLastStep) {
            const validation = validateRequiredStepsBeforeBooking();

            if (!validation.isValid) {
                dispatch(goToStep(validation.failingStep));
                setPendingValidation(validation.failingStep);

                return;
            }
        }

        try {
            if (stepperFooterRef?.current?.triggerNext) {
                if (isLastStep) setIsBookingQuote(true);
                await stepperFooterRef.current.triggerNext();
            }

            // Mark step as completed and advance
            dispatch(completeStep(currentStep));

            if (!isLastStep) {
                dispatch(nextStep());
            }
        } catch {
            // Validation failed — stay on current step
        } finally {
            if (isLastStep) setIsBookingQuote(false);
        }
    };

    // Map step key to component so the correct screen renders regardless of filtered step count
    const stepComponentsByKey: Record<string, React.ReactNode> = {
        [BOOKING_KEYS.SHIPPER_DETAILS]: <ShipperConsigneeDetails isShipper={true} key="shipper" stepperFooterRef={stepperFooterRef} onEditStateChange={setIsStepFormEditing} />,
        [BOOKING_KEYS.CONSIGNEE_DETAILS]: <ShipperConsigneeDetails isShipper={false} key="consignee" stepperFooterRef={stepperFooterRef} onEditStateChange={setIsStepFormEditing} />,
        [BOOKING_KEYS.ORDER_DETAILS]: <OrderDetails stepperFooterRef={stepperFooterRef} />,
        [BOOKING_KEYS.LOAD_DETAILS]: <Dimensions stepperFooterRef={stepperFooterRef} />,
        [BOOKING_KEYS.DOCUMENTS]: <UploadDocuments stepperFooterRef={stepperFooterRef} />,
        [BOOKING_KEYS.BOL_REQUIREMENTS]: <BolRequirements stepperFooterRef={stepperFooterRef} />,
        [BOOKING_KEYS.REVIEW_CONFIRM]: <ReviewFinalize stepperFooterRef={stepperFooterRef} />,
    };

    // Review & Confirm's cards carry their own internal padding, so the sticky header (title +
    // Back/Book Quote) needs the SAME outer inset as the card stack below it, or the two edges
    // don't line up. Scoped to this one step so every other step's flush header stays untouched.
    // stepper-box-wrap's own gap-x-5/pr-5 is cancelled here rather than left in place, so this
    // padding REPLACES that default inset instead of stacking on top of it (matching wal-frontend-v2's
    // OrderSplitStepper, which does the same gap-0 pr-0 + matching header/content padding for its
    // review step).
    const isReviewStep = currentStepInfo?.key === BOOKING_KEYS.REVIEW_CONFIRM;
    const reviewStepPaddingClass = isReviewStep ? 'px-[var(--stepper-content-right-spacing)]' : '';
    const reviewStepWrapClass = isReviewStep ? 'gap-0 pr-0' : '';

    const headerButtons = (
        <div className="flex items-center gap-x-2">
            {currentStep > 1 && (
                <ButtonCmp type="button" className="btn_secondary_black" onClick={handleBack} disabled={isBookingQuote}>
                    {t('Common.Back')}
                </ButtonCmp>
            )}
            <ButtonCmp type="button" className="btn-outline-primary" onClick={handleNext} disabled={isBookingQuote || isStepFormEditing} loading={isBookingQuote}>
                {currentStep === steps.length ? t('BookOrder.Booking_Stepper.title') : t('Common.Next')}
            </ButtonCmp>
        </div>
    );

    return (
        <PageSectionLayout
            headerClassName="stepper-container"
            contentClassName="stepper-container overflow-y-auto"
            header={
                <Header
                    title={
                        <div className="flex items-center gap-x-2.5">
                            <ButtonCmp className="btn_secondary_black" size="square-sm" onClick={handleExitFlow}>
                                <ArrowLeft className="w-4 h-4" />
                            </ButtonCmp>
                            {t('BookOrder.Booking_Stepper.title')}
                        </div>
                    }
                    isShowNotificationBox
                />
            }
        >
            <StepperLayout
                Sidebar={<Sidebar onStepNavigate={handleSidebarStepNavigate} isCrossBorder={isCrossBorder} stops={sortedStops} />}
                title={currentStepInfo?.screenTitle}
                description={currentStepInfo?.screenDescription}
                headerChildren={headerButtons}
                headerClassName={reviewStepPaddingClass}
                childrenClassName={reviewStepPaddingClass}
                stepperWrapClassName={reviewStepWrapClass}
                contentWrapClassName="pb-2"
            >
                {currentStepInfo &&
                    (currentStepInfo.key === 'stop' ? (
                        <BookingStopDetails
                            key={`stop-${currentStepInfo.position}`}
                            stop={middleStops.find((stop: any) => stop.position === currentStepInfo.position)}
                            stepperFooterRef={stepperFooterRef}
                        />
                    ) : (
                        stepComponentsByKey[currentStepInfo.key]
                    ))}
            </StepperLayout>
        </PageSectionLayout>
    );
};

export default BookingStepper;
